import {trpc} from "@/lib/requests/index"
import {useEffect} from "react"
import {useRouter, NextRouter} from "next/router"
import type {UserAuth} from "./server"
import Router from "next/router"
import cookie from "js-cookie"
import {AUTH_TOKEN_KEY} from "@/lib/consts"

export type AuthInvalidCallback = (router: NextRouter) => void

export function useClientSideAuth(
    requiredAuth: UserAuth,
    onInvalid: AuthInvalidCallback | null = null
): UserAuth | "pending" {
    const router = useRouter()
    const userAuth = trpc.useQuery(
        ["user.auth"], {retry: true}
    )

    useEffect(() => {
        const authCookie = getClientAuthCookie()
        if (authCookie) {
            return
        }
        const targetRoute = getTargetRedirect(requiredAuth)
        router.push(targetRoute)
    }, [])
    
    useEffect(() => {
        if (
            userAuth.data === undefined 
            && userAuth.failureCount < 1
        ) {
            return
        }
        let authValid = true
        const candidate = userAuth.data || "none"
        switch (requiredAuth) {
            case "user":
                if (candidate === "none") {
                    authValid = false
                }
                break
            case "admin":
                if (candidate !== "admin") {
                    authValid = false
                }
                break
            default:
                break
        }

        if (authValid) {
            return
        }
        if (onInvalid) {
            onInvalid(router)
            return
        }
        const targetRoute = getTargetRedirect(requiredAuth)
        router.push(targetRoute)
    }, [userAuth])

    return userAuth.data || "pending"
}

function getTargetRedirect(requiredAuth: UserAuth): string {
    if (requiredAuth === "admin") {
        return "/app"
    }
    const currentUrl = location.href
    const encoded = encodeURIComponent(currentUrl)
    return `${process.env.NEXT_PUBLIC_AUTH_BASE_URL}/?redirect=${encoded}&msg=no_auth`
}

export type LoginConfig = {
    token: string, 
    redirect?: string
}

export function login(config: LoginConfig) {
    const {token, redirect = "/app"} = config
    cookie.set(AUTH_TOKEN_KEY, token, { expires: 1 })
    Router.push(redirect)
}

export function logout() {
    cookie.remove(AUTH_TOKEN_KEY)
    window.localStorage.setItem("logout", String(Date.now()))
    Router.push("/")
}
  
export function getClientAuthCookie(): string {
    const auth = cookie.get(AUTH_TOKEN_KEY)
    return auth || ""
}